if (!customElements.get("fourpaw-cart-upsell")) {
  class FourPawCartUpsell extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.button = this.querySelector("[data-fourpaw-cart-upsell-button]");
      this.status = this.querySelector("[data-fourpaw-cart-upsell-status]");
      this.variantSelect = this.querySelector("[data-fourpaw-cart-upsell-variant]");
      this.image = this.querySelector("[data-fourpaw-cart-upsell-image]");
      this.currentPrice = this.querySelector("[data-fourpaw-cart-upsell-price-current]");
      this.originalPrice = this.querySelector("[data-fourpaw-cart-upsell-price-original]");
      this.discountBadge = this.querySelector("[data-fourpaw-cart-upsell-discount]");
      this.onAddAccessory = () => this.addAccessory();
      this.onVariantChange = () => this.syncSelectedVariant();
      this.button?.addEventListener("click", this.onAddAccessory);
      this.variantSelect?.addEventListener("change", this.onVariantChange);
      this.syncSelectedVariant();
      this.viewObserver = new IntersectionObserver((entries) => {
        if (window.fourPawCartSuggestionDismissed || !entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5)) return;
        const key = `${this.dataset.offerType}:${this.dataset.variantId}`;
        window.fourPawViewedCartOffers ||= new Set();
        if (!window.fourPawViewedCartOffers.has(key)) {
          window.fourPawViewedCartOffers.add(key);
          document.dispatchEvent(new CustomEvent("fourpaw:cart-upsell-viewed", {
            detail: { accessory: this.dataset.offerType },
          }));
        }
        this.viewObserver.disconnect();
      }, { threshold: 0.5 });
      this.viewObserver.observe(this);
    }

    disconnectedCallback() {
      this.button?.removeEventListener("click", this.onAddAccessory);
      this.variantSelect?.removeEventListener("change", this.onVariantChange);
      this.viewObserver?.disconnect();
      this.initialized = false;
    }

    syncSelectedVariant() {
      const selectedOption = this.variantSelect?.selectedOptions?.[0];
      if (!selectedOption) return;

      this.dataset.variantId = selectedOption.value;
      if (selectedOption.dataset.expectedCents) this.dataset.expectedCents = selectedOption.dataset.expectedCents;
      if (this.image && selectedOption.dataset.imageSrc) {
        this.image.src = selectedOption.dataset.imageSrc;
        this.image.alt = selectedOption.dataset.imageAlt || this.image.alt;
      }
      if (this.currentPrice && selectedOption.dataset.currentPrice) {
        this.currentPrice.textContent = selectedOption.dataset.currentPrice;
      }
      if (this.originalPrice) {
        this.originalPrice.textContent = selectedOption.dataset.originalPrice || "";
        this.originalPrice.classList.toggle("is-hidden", !selectedOption.dataset.originalPrice);
      }
      if (this.discountBadge) {
        this.discountBadge.textContent = selectedOption.dataset.discountLabel || "";
        this.discountBadge.classList.toggle("is-hidden", !selectedOption.dataset.discountLabel);
      }
      if (this.button && selectedOption.dataset.buttonLabel) {
        this.button.textContent = selectedOption.dataset.buttonLabel;
      }
    }

    matchingAccessoryProperties() {
      let sourceContract;
      let slotMap;
      try {
        sourceContract = JSON.parse(this.dataset.kachingContract || "{}");
        slotMap = JSON.parse(this.dataset.kachingSlotMap || "{}");
      } catch {
        throw new Error("The matching add-on is unavailable.");
      }
      if (!sourceContract || typeof sourceContract !== "object" || !slotMap || typeof slotMap !== "object") {
        throw new Error("The matching add-on is unavailable.");
      }

      const requiredFields = ["id", "deal", "pp", "v", "bid"];
      const hasCompleteContract = requiredFields.every((field) => (
        sourceContract[field] !== undefined &&
        sourceContract[field] !== null &&
        String(sourceContract[field]).trim() !== ""
      ));
      const packageGroup = String(this.dataset.packageGroup || "").trim();
      const slotKey = { leash: "b", collar: "c" }[this.dataset.offerType];
      if (!slotKey) throw new Error("The matching add-on is unavailable.");
      const offerSlots = slotMap[slotKey];
      let packagePosition = 0;

      if (!sourceContract.main) {
        const harnessSlot = String(sourceContract.upsell || "");
        packagePosition = Array.isArray(slotMap.h)
          ? slotMap.h.map(String).indexOf(harnessSlot) + 1
          : 0;
        if (packagePosition < 1) {
          throw new Error("The matching add-on is unavailable.");
        }
      }

      const mappedUpsellId = Array.isArray(offerSlots) ? offerSlots[packagePosition] : "";
      const fallbackUpsellId = packagePosition === 0 ? this.dataset.kachingUpsellId : "";
      const upsellId = String(mappedUpsellId || fallbackUpsellId || "").trim();
      if (!hasCompleteContract || !packageGroup || !upsellId) {
        throw new Error("The matching add-on is unavailable.");
      }

      // Preserve the exact Kaching contract shape/order from the harness and
      // only change its role. This makes the first Shopify write final instead
      // of forcing the cart normalizer to repair the new line afterwards.
      const accessoryContract = { ...sourceContract };
      delete accessoryContract.main;
      delete accessoryContract.upsell;
      accessoryContract.upsell = upsellId;

      return {
        __kaching_bundles: JSON.stringify(accessoryContract),
        "_Package ID": packageGroup,
        "_Harness reference": `SW-${packageGroup.slice(-6).toUpperCase()}`,
      };
    }

    expectedPriceCents() {
      const expectedValue = String(this.dataset.expectedCents ?? "").trim();
      const expectedPrice = Number(expectedValue);
      if (!expectedValue || !Number.isSafeInteger(expectedPrice) || expectedPrice < 0) {
        throw new Error("The matching add-on price is unavailable.");
      }
      return expectedPrice;
    }

    matchingAccessoryLine(cart, expectedProperties) {
      const variantId = Number(this.dataset.variantId);
      const packageGroup = String(expectedProperties?.["_Package ID"] || "");
      const candidates = (cart?.items || []).filter((item) => (
        Number(item.variant_id || item.id) === variantId &&
        String(item.properties?.["_Package ID"] || "") === packageGroup
      ));
      if (candidates.length < 1) return null;

      let expectedContract;
      try {
        expectedContract = JSON.parse(expectedProperties?.__kaching_bundles || "{}");
      } catch {
        return candidates.length === 1 ? candidates[0] : null;
      }
      const exact = candidates.find((item) => {
        try {
          const contract = JSON.parse(item.properties?.__kaching_bundles || "{}");
          return String(contract.id || "") === String(expectedContract.id || "") &&
            String(contract.upsell || "") === String(expectedContract.upsell || "");
        } catch {
          return false;
        }
      });
      return exact || (candidates.length === 1 ? candidates[0] : null);
    }

    async verifyMatchingAccessoryPrice(controller, cart, expectedProperties) {
      const expectedPrice = this.expectedPriceCents();
      const isPromisedPrice = (item) => {
        const finalPrice = Number(item?.final_price);
        return Number.isSafeInteger(finalPrice) && finalPrice <= expectedPrice;
      };
      let accessory = this.matchingAccessoryLine(cart, expectedProperties);
      if (!accessory) throw new Error("The matching add-on could not be verified.");
      if (isPromisedPrice(accessory)) return cart;

      const root = window.Shopify?.routes?.root || "/";
      const changeUrl = `${root}cart/change.js`;
      const recalculatedCart = await controller.fourPawCartRequest(changeUrl, {
        id: accessory.key,
        quantity: accessory.quantity,
        properties: { ...(accessory.properties || {}) },
      });
      const recalculated = await controller.normalizeFourPawSafeWalkCart(recalculatedCart);
      accessory = this.matchingAccessoryLine(recalculated.cart, expectedProperties);
      if (accessory && isPromisedPrice(accessory)) return recalculated.cart;
      if (!accessory) throw new Error("The matching add-on could not be verified.");

      let recoveredCart = recalculated.cart;
      recoveredCart = await controller.fourPawCartRequest(changeUrl, {
        id: accessory.key,
        quantity: 0,
      });
      recoveredCart = (await controller.normalizeFourPawSafeWalkCart(recoveredCart)).cart;
      await controller.fourPawRefreshCart(recoveredCart, true);
      const error = new Error("The add-on discount could not be applied. Please try again.");
      error.fourPawCartRecovered = true;
      throw error;
    }

    async addAccessory() {
      if (!this.button || this.button.disabled) return;
      await window.fourPawCartWork?.settled;
      if (!this.isConnected) return;
      // An interrupted response may already have added this item. Recover the
      // current cart before allowing another mutation from a stale offer.
      if (window.fourPawCartWork?.needsSync) {
        await document.querySelector("cart-drawer")?.synchronizeCart?.(true);
        return;
      }
      const finish = window.fourPawCartWork?.begin();
      if (window.fourPawCartWork && !finish) return;

      const originalLabel = this.button.textContent.trim();
      this.button.disabled = true;
      this.button.textContent = this.dataset.loadingLabel || "Adding…";
      if (this.status) this.status.textContent = "";
      document.dispatchEvent(
        new CustomEvent("fourpaw:cart-upsell-attempted", {
          detail: { accessory: this.dataset.offerType },
        }),
      );

      let requestStarted = false;
      let responseReceived = false;
      try {
        const addMode = this.dataset.addMode || "kaching";
        const variantId = Number(this.dataset.variantId);
        if (!variantId) {
          throw new Error("The selected add-on is unavailable.");
        }

        const item = {
          id: variantId,
          quantity: 1,
        };

        if (addMode === "kaching") {
          item.properties = this.matchingAccessoryProperties();
          item.properties["_FourPaw offer price"] = String(this.expectedPriceCents());
          item.properties["_FourPaw offer currency"] = window.Shopify?.currency?.active || "";
        }

        const root = window.Shopify?.routes?.root || "/";
        const addPath = addMode === "kaching" ? "cart/add.js?kaching_bundles=true&partial=true" : "cart/add.js";
        const addUrl = new URL(`${root}${addPath}`, window.location.origin);
        const previewThemeId =
          window.Shopify?.theme?.role === "unpublished" ? String(window.Shopify.theme.id || "") : "";

        const request = window.fourPawCartFetch || window.fetch.bind(window);
        requestStarted = true;
        const response = await request(addUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
            Accept: "application/json",
          },
          body: JSON.stringify({
            items: [item],
            sections: ["cart-drawer", "cart-icon-bubble"],
            sections_url: window.location.pathname,
          }),
        });
        const data = await response.json();
        responseReceived = true;
        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || "Unable to add this item.");
        }

        if (previewThemeId) {
          try {
            const previewSectionsUrl = new URL(root, window.location.origin);
            previewSectionsUrl.searchParams.set("sections", "cart-drawer,cart-icon-bubble");
            previewSectionsUrl.searchParams.set("preview_theme_id", previewThemeId);
            const previewSectionsResponse = await request(previewSectionsUrl);
            if (previewSectionsResponse.ok) {
              const previewSections = await previewSectionsResponse.json();
              if (previewSections && typeof previewSections === "object") data.sections = previewSections;
            }
          } catch (previewError) {
            this.dataset.previewRenderError = previewError instanceof Error ? previewError.message : String(previewError);
          }
        }

        const cartDrawer = document.querySelector("cart-drawer");
        if (cartDrawer?.renderContents && data.sections) {
          cartDrawer.renderContents(data, false, true);
        }
        const controller = cartDrawer?.querySelector("cart-drawer-items") || document.querySelector("cart-items");
        if (controller?.normalizeFourPawSafeWalkCart && controller?.fourPawRefreshCart) {
          const normalized = await controller.normalizeFourPawSafeWalkCart();
          const finalCart = addMode === "kaching"
            ? await this.verifyMatchingAccessoryPrice(controller, normalized.cart, item.properties)
            : normalized.cart;
          await controller.fourPawRefreshCart(finalCart, true);
        this.button.textContent = this.dataset.addedLabel || "Added!";
        document.dispatchEvent(
          new CustomEvent("fourpaw:cart-upsell-added", {
            detail: { accessory: this.dataset.offerType },
          }),
        );

        } else if (!cartDrawer?.renderContents || !data.sections) {
          window.location.assign(window.routes?.cart_url || "/cart");
        }
      } catch (error) {
        if (!error?.fourPawCartRecovered) window.fourPawCartWork?.fail();
        this.button.disabled = false;
        this.button.textContent = originalLabel;
        this.dataset.errorDetail = error instanceof Error ? error.message : String(error);
        const currentUpsell = Array.from(document.querySelectorAll("fourpaw-cart-upsell")).find(
          (candidate) => candidate.dataset.offerType === this.dataset.offerType,
        );
        const visibleStatus = currentUpsell?.querySelector("[data-fourpaw-cart-upsell-status]") || this.status;
        if (visibleStatus) visibleStatus.textContent = this.dataset.errorLabel || error.message || "Could Not Add It. Please Try Again.";
        document.dispatchEvent(
          new CustomEvent("fourpaw:cart-upsell-failed", {
            detail: { accessory: this.dataset.offerType },
          }),
        );
      } finally {
        finish?.();
        // Reconcile an ambiguous write once, without re-sending the add. If the
        // read also fails, needsSync keeps subsequent retries behind recovery.
        if (requestStarted && !responseReceived && window.fourPawCartWork?.needsSync) {
          await document.querySelector("cart-drawer")?.synchronizeCart?.(true);
        }
      }
    }
  }

  customElements.define("fourpaw-cart-upsell", FourPawCartUpsell);
}

// Dismissal lasts for this page visit, including cart section replacements.
if (!customElements.get("fourpaw-cart-suggestion")) {
  customElements.define("fourpaw-cart-suggestion", class extends HTMLElement {
    connectedCallback() {
      this.hidden = Boolean(window.fourPawCartSuggestionDismissed);
      this.onDismiss = () => {
        const restoreFocus = this.contains(document.activeElement);
        window.fourPawCartSuggestionDismissed = true;
        this.hidden = true;
        if (restoreFocus) this.closest("cart-drawer")?.querySelector('[name="checkout"]')?.focus({ preventScroll: true });
      };
      this.querySelector("[data-fourpaw-upsell-dismiss]")?.addEventListener("click", this.onDismiss);
    }
    disconnectedCallback() {
      this.querySelector("[data-fourpaw-upsell-dismiss]")?.removeEventListener("click", this.onDismiss);
    }
  });
}
